import {
  Box,
  Chip,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import moment from "moment";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import GlobalButton from "../../../components/button";
import CentralCalendarComponent from "../../../components/centralCalendar";
import CustomCheckBox from "../../../components/checkbox";
import AppHeader from "../../../components/header";
import Label from "../../../components/label";
import AppLoader from "../../../components/loader";
import TextInput from "../../../components/textInput";
import {
  agentMarkDaysOffForAll,
  getAgentOffDaysForAll,
  getAllAgents,
} from "../../../services/AgentApi";
import { handleOpenSnackBar } from "../../../utils/helper";
import CustomDropDown from "../../../components/select";

const useStyle = {
  datePickerStyle: {
    width: "100%",
    "&.MuiFormControl-root .MuiInputBase-root": {
      borderRadius: "4px",
      backgroundColor: "#d4ecff87",
    },
  },
};

const dropDownStyle = {
  display: "flex",
  overflowY: "auto",
  gap: 0.5,

  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
    display: "none",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "primary.main",
    // borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "secondary.dark",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "text.coolGrey",
  },
};

const tableHeader = ["Date", "Day", "Reason", "Actions"];

const displayRows = ["agent_off_date", "off_day", "reason", "actions"];

const MarkOffDaysPage = ({}) => {
  const params = useParams();
  const [reason, setReason] = useState("");
  const [offDays, setOffDays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [singlesOffDays, setSingleOffDays] = useState([]);
  const [dumyDate, setDumyDate] = useState(null);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [agentsOffDays, setAgentsOffDays] = useState([]);
  const [type, setTyp] = useState("All");


  useEffect(() => {
    handleFetchAllGents();
  }, []);

  const handleFetchAllGents = async () => {
    try {
      setIsLoading(true);

      const [agentsRes, offDaysRes] = await Promise.all([
        getAllAgents(),
        getAgentOffDaysForAll(),
      ]);

      if (
        agentsRes?.data.status === "success" &&
        offDaysRes?.data.status === "success"
      ) {
        const agents = agentsRes.data.data;
        const offDays = offDaysRes.data.data;

        const newData = offDays?.map((x) => {
          return {
            id: x._id,
            title: x.username || "NA",
            start: new Date(x.offDays.date),
            end: new Date(x.offDays.date),
            colorCode: x.colorCode,
            reason: x.offDays.reason,
            offDate: x.offDays.date,
          };
        });
        setAllAgents(agents);
        setAgentsOffDays(newData);
      } else {
        const errorMsg = agentsRes?.data.message || offDaysRes?.data.message;
        handleOpenSnackBar("error", errorMsg);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDateSelection = (date) => {
    if (!date) return;

    // Fix timezone issue by formatting date in 'YYYY-MM-DD'
    const dateString = moment(date.$d).format("YYYY-MM-DD");

    if (offDays.some((d) => moment(d.date).format("YYYY-MM-DD") === dateString))
      return;

    if (selectedDates.includes(dateString)) {
      setSelectedDates(selectedDates.filter((d) => d !== dateString));
    } else {
      setSelectedDates([...selectedDates, dateString]);
    }
  };

  const markOffDays = async () => {
    if (selectedDates.length === 0)
      return handleOpenSnackBar("error", "Please select at least one date");
    if (!reason) return handleOpenSnackBar("error", "Reason is required");

    const payload = {
      agents: selectedAgents,
      dates: selectedDates,
      reason,
    };

    try {
      setIsLoading(true);
      const response = await agentMarkDaysOffForAll(payload);
      if (response.data.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        handleFetchAllGents();
        setSelectedDates([]);
        setReason("");
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppHeader>
      <AppLoader isVisible={isLoading} />
      <Typography
        variant="primaryText"
        component={"h1"}
        fontSize={{ xs: "1.5rem", md: "2.125rem" }}
        gutterBottom
      >
        Mark Off Days For Agents
      </Typography>

      <Grid container spacing={2} alignItems={"end"}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Label title={"Select Type"} htmlFor={"type"} />
          <CustomDropDown
            id={"type"}
            required
            variant={"outlined"}
            value={type}
            onChange={(e) => {
              setTyp(e.target.value);
              setSelectedAgents([]);
            }}
            stylePropos={{
              "& .MuiSelect-select": {
                padding: "14px 14px 14px 25px",
                backgroundColor: "text.appLightBlue",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
            }}
          >
            {["All", "Select Agents"].map((val, ind) => {
              return (
                <MenuItem key={ind} value={val}>
                  {val}
                </MenuItem>
              );
            })}
          </CustomDropDown>
        </Grid>
        {type === "Select Agents" && (
          <Grid item xs={12} sm={6} md={2.4}>
            <Stack direction={"row"} alignItems={"center"} gap={2} mb={1}>
              <Label title={"Select Agents"} style={{ mb: 0 }} />
            </Stack>
            <Select
              variant="outlined"
              fullWidth
              size="small"
              multiple
              value={selectedAgents}
              sx={{
                "& .MuiSelect-select": {
                  padding: "14px 14px 14px 25px",
                  backgroundColor: "text.appLightBlue",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
              }}
              onChange={(e) => {
                setSelectedAgents(e.target.value);
              }}
              renderValue={(selected) => (
                <Box sx={{ ...dropDownStyle }}>
                  {selected.map((id, index) => {
                    const agent = allAgents.find((agent) => agent._id === id);
                    return (
                      <Chip
                        sx={{
                          height: "auto",
                        }}
                        key={id}
                        label={agent?.username}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {" "}
              {allAgents.map((val, index) => (
                <MenuItem key={index} value={val._id}>
                  {val.username}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={2.4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Label title={"Select off days"} />
            <MobileDatePicker
              // label="Select Off Days"
              value={dumyDate}
              onChange={(newValue) => {
                toggleDateSelection(newValue);
                setDumyDate(newValue);
              }}
              onClose={() => setDumyDate(null)}
              shouldDisableDate={(date) => {
                const day = date.day(); // 0 = Sunday, 6 = Saturday
                const formattedDate = date.format("YYYY-MM-DD"); // Format to match offDays array

                // Agar date weekend hai ya pehle se off days me hai to disable
                return (
                  day === 0 ||
                  day === 6 ||
                  singlesOffDays.includes(formattedDate)
                );
              }}
              sx={useStyle.datePickerStyle}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Label title={"Add reason"} />
          <TextInput
            variant="outlined"
            id={"agent-reason"}
            type={"text"}
            required
            placeholder={"Enter reason for off days"}
            value={reason}
            name={"reason"}
            onChange={(e) => setReason(e.target.value)}
            style={{
              "& .MuiInputBase-root": {
                borderRadius: "4px",
                backgroundColor: "#d4ecff87",
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <GlobalButton
            variant={"custom_gradient"}
            handleClickBtn={markOffDays}
            title={"Mark Off Days"}
            style={{ height: 50, borderRadius: "4px" }}
          />
        </Grid>
        {selectedDates?.length > 0 && (
          <Grid item xs={12}>
            <Box my={2}>
              <Typography>Selected Dates</Typography>
              <Stack
                mt={1}
                gap={1}
                direction={"row"}
                alignItems={"center"}
                flexWrap={"wrap"}
              >
                {selectedDates.map((date, index) => (
                  <Chip
                    key={index}
                    label={moment(date).format("DD.MM.YY")}
                    color="primary"
                    onDelete={() =>
                      setSelectedDates(selectedDates.filter((d) => d !== date))
                    }
                  />
                ))}
              </Stack>
            </Box>
          </Grid>
        )}
      </Grid>

      <Box mt={3}></Box>
      <CentralCalendarComponent data={agentsOffDays} title={"Agent Off Days"} />
    </AppHeader>
  );
};

export default MarkOffDaysPage;
